
	import {
		defineComponent,
		nextTick,
		onMounted,
		onUnmounted,
		ref,
		watch,
	} from 'vue';
	import type { ElInput } from 'element-plus';
	import { useRoute } from 'vue-router';
	import { useCreatingForm } from '@/core/hooks/use-creating-form';
	import { IDanhMucToaXeResponseBase } from '@/core/interfaces/ApiResponses';
	import { useRouterCustom } from '@/core/hooks/use-router-custom';
	import {
		setBreadcrumbLinks,
		setCurrentPageTitle,
	} from '@/core/helpers/breadcrumb';
	import {
		lengthValidationRules,
		swalErrNotification,
		swalNotification,
	} from '@/core/helpers/utils';
	import { useButtonCustom } from '@/core/hooks/use-button-custom';

	import DanhMucToaXeService from '@/core/services/DanhMucToaXe.service';
	import PageContentBase from '@/layout/_shared/PageContentBase.vue';
	import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
	import DivRow from '@/layout/_shared/DivRow.vue';
	import DivCol from '@/layout/_shared/DivCol.vue';
	import LabelCustom from '@/components/shared/label-custom/LabelCustom.vue';
	import useFormInput from '@/core/hooks/use-form-input';
	import { mapMutations } from 'vuex';
	import { resolveListPageForRoute } from '@/core/helpers/list-page-context';
	import { usePagination } from '@/core/hooks/use-pagination';
	import DanhMucToaXeTable from '@/views/crafted/pages/danh-muc-toa-xe/danh-muc-toa-xe-table/DanhMucToaXeTable.vue';
	import Pagination from '@/components/shared/pagination/Pagination.vue';

	const MA_DANH_MUC_PREVIEW_INTERVAL_MS = 3000;

	export default defineComponent({
		name: 'danh-muc-toa-xe-form',

		components: {
			ButtonCustom,
			DanhMucToaXeTable,
			DivCol,
			DivRow,
			LabelCustom,
			PageContentBase,
			Pagination,
		},

		setup() {
			const { isCreating, id, formRef } = useCreatingForm();
			const { goBack, replace } = useRouterCustom();
			const { ButtonTypeColors, ButtonsType } = useButtonCustom();
			const { focusFirstErrInputV2 } = useFormInput();
			const {
				currentPage,
				totalItems,
				perPage,
				loading: paginationLoading,
			} = usePagination();
			const route = useRoute();
			currentPage.value = resolveListPageForRoute(route.query, {
				isCreate: isCreating.value,
			});

			const danhMucToaXe = ref<IDanhMucToaXeResponseBase | null>(null);
			const danhMucToaXeItems = ref<IDanhMucToaXeResponseBase[]>([]);
			const danhMucToaXeForm = ref({
				ma_danh_muc: '',
				ten_danh_muc: '',
				mo_ta: '',
			});
			const tenDanhMucInputRef = ref<InstanceType<typeof ElInput>>();
			let maDanhMucPreviewIntervalId: ReturnType<typeof setInterval> | null =
				null;

			const focusTenDanhMucInput = async () => {
				await nextTick();
				tenDanhMucInputRef.value?.focus();
				tenDanhMucInputRef.value?.select?.();
			};

			const fetchPreviewMaDanhMuc = async () => {
				if (!isCreating.value) {
					return;
				}

				try {
					const {
						data: {
							data: { ma_danh_muc },
						},
					} = await DanhMucToaXeService.generateMaDanhMuc({
						id_cha: 0,
					});

					danhMucToaXeForm.value.ma_danh_muc = ma_danh_muc;
				} catch {
					// Preview polling should not interrupt form usage.
				}
			};

			const startMaDanhMucPreviewPolling = () => {
				if (!isCreating.value || maDanhMucPreviewIntervalId) {
					return;
				}

				fetchPreviewMaDanhMuc();
				maDanhMucPreviewIntervalId = setInterval(
					fetchPreviewMaDanhMuc,
					MA_DANH_MUC_PREVIEW_INTERVAL_MS,
				);
			};

			const stopMaDanhMucPreviewPolling = () => {
				if (!maDanhMucPreviewIntervalId) {
					return;
				}

				clearInterval(maDanhMucPreviewIntervalId);
				maDanhMucPreviewIntervalId = null;
			};

			const loadDanhMucDetail = async () => {
				if (isCreating.value || !id.value) {
					danhMucToaXeForm.value = {
						ma_danh_muc: '',
						ten_danh_muc: '',
						mo_ta: '',
					};
					document.title = 'Tạo mới - Nhóm toa xe - Đường sắt Hà Nội';
					setCurrentPageTitle('Tạo mới nhóm toa xe');
					startMaDanhMucPreviewPolling();

					return;
				}

				stopMaDanhMucPreviewPolling();

				const {
					data: {
						data: { ten_danh_muc, mo_ta, ma_danh_muc },
					},
				} = await DanhMucToaXeService.get(id.value);

				danhMucToaXeForm.value.ten_danh_muc = ten_danh_muc;
				danhMucToaXeForm.value.ma_danh_muc = ma_danh_muc;
				danhMucToaXeForm.value.mo_ta = mo_ta;
				document.title = `${ten_danh_muc} (${ma_danh_muc}) - Chỉnh sửa - Nhóm toa xe - Đường sắt Hà Nội`;
				setCurrentPageTitle(`${ten_danh_muc} (${ma_danh_muc})`);
			};

			onMounted(async () => {
				setBreadcrumbLinks([
					{
						text: 'Nhóm toa xe',
						routeData: {
							name: 'danh-muc-toa-xe',
						},
					},
					...(isCreating.value
						? []
						: [
								{
									text: 'Chi tiết',
									routeData: {
										name: 'chi-tiet-danh-muc-toa-xe',
										params: {
											id: id.value,
										},
									},
								},
							]),
					{
						text: isCreating.value ? 'Tạo mới' : 'Chỉnh sửa',
					},
				]);

				await loadDanhMucDetail();
			});

			onUnmounted(() => {
				stopMaDanhMucPreviewPolling();
			});

			watch(
				() => [id.value, isCreating.value] as const,
				async ([nextId, creating], [prevId, prevCreating]) => {
					if (creating) {
						currentPage.value = resolveListPageForRoute(route.query, {
							isCreate: true,
						});

						if (!prevCreating) {
							await loadDanhMucDetail();
							await focusTenDanhMucInput();
						}

						return;
					}

					if (!nextId || (nextId === prevId && !prevCreating)) {
						return;
					}

					currentPage.value = resolveListPageForRoute(route.query, {
						isCreate: false,
					});
					await loadDanhMucDetail();
					await focusTenDanhMucInput();
				},
			);

			return {
				ButtonTypeColors,
				ButtonsType,
				currentPage,
				danhMucToaXeItems,
				danhMucToaXeId: id,
				fetchPreviewMaDanhMuc,
				focusFirstErrInputV2,
				focusTenDanhMucInput,
				loading: paginationLoading,
				danhMucToaXeForm,
				isCreating,
				formRef,
				danhMucToaXe,
				goBack,
				perPage,
				replace,
				tenDanhMucInputRef,
				totalItems,
			};
		},

		data() {
			return {
				rules: {
					ten_danh_muc: [
						...lengthValidationRules({
							name: 'Tên nhóm toa xe',
						}),
					],
					mo_ta: [
						...lengthValidationRules({
							isRequired: false,
							max: 160,
							name: 'Mô tả nhóm toa xe',
						}),
					],
				},
			};
		},

		async mounted() {
			await this.fetchData();
			await this.focusTenDanhMucInput();
		},

		watch: {
			async currentPage() {
				await this.fetchData();
			},

			async perPage() {
				if (this.currentPage === 1) {
					await this.fetchData();

					return;
				}

				this.currentPage = 1;
			},
		},

		methods: {
			...mapMutations('ConfirmationDialogModule', {
				setDialog: 'setDialog',
				closeDialog: 'closeDialog',
			}),
			async fetchData() {
				try {
					this.loading = true;

					const {
						data: {
							data: { data, total },
						},
					} = await DanhMucToaXeService.list(
						this.currentPage,
						this.perPage,
					);

					this.danhMucToaXeItems = data;
					this.totalItems = total;
					this.loading = false;
				} catch {
					await swalNotification(
						'Có lỗi xảy ra khi lấy danh sách nhóm toa xe',
						'error',
					);
					this.loading = false;
				}
			},

			async refreshFirstPage() {
				if (this.currentPage !== 1) {
					this.currentPage = 1;

					return;
				}

				await this.fetchData();
			},

			async clear() {
				this.danhMucToaXeForm.ten_danh_muc = '';
				this.danhMucToaXeForm.mo_ta = '';

				await nextTick();
				this.formRef?.clearValidate();

				if (this.isCreating) {
					await this.fetchPreviewMaDanhMuc();
				}
			},

			submit(shouldContinue = false) {
				if (!this.formRef) {
					return;
				}

				this.formRef.validate(async (valid) => {
					if (valid) {
						this.loading = false;

						this.setDialog({
							message: this.isCreating
								? shouldContinue
									? 'Tạo mới nhóm toa xe với những thông tin này và tiếp tục tạo mới không?'
									: 'Tạo mới nhóm toa xe với những thông tin này không?'
								: 'Bạn có chắc chắn muốn lưu chỉnh sửa cho nhóm toa xe này không?',
							onConfirm: async () => {
								this.closeDialog();

								try {
									if (this.isCreating) {
										const {
											data: {
												data: { id },
											},
										} = await DanhMucToaXeService.create(
											this.danhMucToaXeForm.ten_danh_muc,
											this.danhMucToaXeForm.mo_ta,
											0,
										);

										await swalNotification('Tạo mới nhóm toa xe thành công');

										if (shouldContinue) {
											this.clear();
											this.formRef?.resetFields();
											await this.fetchPreviewMaDanhMuc();
											await this.refreshFirstPage();

											return;
										}

										this.danhMucToaXeId = id;
									} else {
										await DanhMucToaXeService.update(
											this.danhMucToaXeId,
											this.danhMucToaXeForm.ten_danh_muc,
											this.danhMucToaXeForm.mo_ta,
											0,
										);

										await swalNotification(
											'Chỉnh sửa nhóm toa xe thành công',
										);

										if (shouldContinue) {
											await this.refreshFirstPage();

											return;
										}
									}

									await this.replace('/quan-ly/danh-muc-toa-xe/danh-sach');
								} catch (error) {
									await swalErrNotification(
										error,
										'Có lỗi xảy ra khi lưu nhóm toa xe',
									);
								}
							},
						});
					} else {
						await this.focusFirstErrInputV2();

						return false;
					}
				});
			},
		},
	});
